import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, Undo2, XCircle, Share2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useWallet } from "../../lib/wallet";
import { useConfig } from "../../lib/queries";
import { fmtAmt, fmtUsd, fmtPrice, short, countdown, STATUS } from "../../lib/format";
import { EscrowSteps } from "./EscrowSteps";
import { Countdown } from "./Countdown";
import { useShare } from "./ShareProvider";

// Pure, testable derivation of everything the ticket needs from a deal + viewer address.
export const deriveTicket = (deal, me) => {
  const live = ["open", "seller_funded", "buyer_funded"].includes(deal.status);
  const isSeller = !!me && deal.seller_address === me;
  const isBuyer = !!me && deal.buyer_address === me;
  const isCreator = !!me && deal.creator_address === me;
  const canFundSeller = live && !deal.seller_funded && (!deal.seller_address || isSeller) && !isBuyer;
  const canFundBuyer = live && !deal.buyer_funded && (!deal.buyer_address || isBuyer) && !isSeller;
  const fundedSide = deal.status === "seller_funded" ? "seller" : deal.status === "buyer_funded" ? "buyer" : null;
  const iAmFunded = !!fundedSide && ((fundedSide === "seller" && isSeller) || (fundedSide === "buyer" && isBuyer));
  const sellerLabel = `${fmtAmt(deal.token_amount)} ${deal.token_symbol}`;
  const buyerLabel = `${fmtUsd(deal.notional_usd)} + ${fmtAmt(deal.fee_cross)} $CROSS`;
  return { live, isSeller, isBuyer, isCreator, canFundSeller, canFundBuyer, fundedSide, iAmFunded, sellerLabel, buyerLabel };
};

const Line = ({ label, value, sub, strong, testId }) => (
  <div className="flex items-start justify-between py-1.5">
    <span className="text-xs text-slate-400">{label}</span>
    <div className="text-right"><div className={`mono text-sm ${strong ? "text-white font-semibold" : "text-slate-100"}`} data-testid={testId}>{value}</div>{sub && <div className="mono text-[10px] text-fog">{sub}</div>}</div>
  </div>
);

const TicketEmpty = ({ cfg }) => (
  <div className="p-5 text-sm text-slate-400 space-y-3" data-testid="escrow-ticket-empty">
    <p className="text-white font-medium">No deal selected.</p>
    <p>Pick a listing on the Deal Board to inspect deposits and fund your side, or switch to <span className="text-violet-300">Propose</span> to post a new offer.</p>
    <ul className="mono text-[11px] text-fog space-y-1 pt-2 border-t border-line">
      <li>· Seller deposits tokens, buyer deposits USD + fee</li>
      <li>· Contract releases both when both are in</li>
      <li>· One side funded, other absent {cfg?.escrow_timeout_hours ?? 24}h → reclaim</li>
      <li>· Untaken listings expire after {cfg?.deal_expiry_hours ?? 72}h</li>
    </ul>
  </div>
);

const TicketHeader = ({ deal, stock, st }) => (
  <div className="px-4 py-3 border-b border-line">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-heading font-semibold text-white text-lg leading-tight flex items-center gap-2 flex-wrap">{deal.token_symbol} <span className="tag border-violet/40 text-violet-300">/{stock}</span> <span className="text-fog text-xs font-normal mono">{deal.id}</span></div>
        <div className="mono text-[11px] text-slate-400 mt-1">{deal.side === "sell" ? "Seller asks" : "Buyer bids"} {fmtPrice(deal.price_usd)}/M · pool {fmtPrice(deal.pool_price_usd)} · <span className={deal.discount_pct > 0 ? "text-pos" : "text-neg"}>{deal.discount_pct > 0 ? "−" : "+"}{Math.abs(deal.discount_pct).toFixed(2)}%</span></div>
      </div>
      <span className={`tag shrink-0 ${st.cls}`} data-testid="escrow-status-tag">{st.label}</span>
    </div>
    {deal.status === "open" && <div className="mono text-[11px] text-fog mt-2">Listing expires in <Countdown seconds={deal.seconds_to_expiry} className="text-slate-300" testId="escrow-expiry-countdown" /></div>}
  </div>
);

const SlippageBadge = ({ deal, stock }) => (
  <div className="rounded border border-violet/40 bg-violet/10 px-3 py-2 flex items-center justify-between" data-testid="escrow-slippage-saved-badge">
    <div><div className="eyebrow text-violet-300">{stock} impact saved vs pool</div><div className="mono text-lg font-semibold text-white">{deal.slippage.saved_usd >= 0 ? "+" : "−"}{fmtUsd(Math.abs(deal.slippage.saved_usd))}</div></div>
    <div className="text-right mono text-[11px] text-fog">{deal.slippage.impact_pct.toFixed(1)}% impact avoided</div>
  </div>
);

const RequiredDeposits = ({ deal, sellerLabel }) => (
  <div>
    <div className="eyebrow mb-1">Required deposits</div>
    <div className="panel px-3 divide-y divide-line/60">
      <Line label={`Seller ${deal.seller_address ? short(deal.seller_address) : "(open)"}`} value={sellerLabel} sub="ERC-20 → escrow contract" testId="escrow-seller-deposit" />
      <Line label={`Buyer ${deal.buyer_address ? short(deal.buyer_address) : "(open)"}`} value={fmtUsd(deal.notional_usd)} sub="USD → escrow contract" testId="escrow-buyer-deposit" />
      <Line label={`Settlement fee ${deal.fee_bps / 100}% · payable in $CROSS`} value={`${fmtAmt(deal.fee_cross)} $CROSS`} sub={`≈ ${fmtUsd(deal.fee_usd, 2)} · charged only on settlement, paid by buyer`} testId="escrow-fee-cross-line" />
      <Line label="Seller receives" value={fmtUsd(deal.notional_usd)} strong />
      <Line label="Buyer receives" value={sellerLabel} strong />
    </div>
  </div>
);

const TimeoutBox = ({ deal, fundedSide, cfg }) => (
  <div className="rounded border border-warn/40 bg-warn/5 px-3 py-2" data-testid="escrow-timeout-box">
    <div className="eyebrow text-warn">Timeout protection</div>
    <div className="text-xs text-slate-300 mt-1">
      {deal.reclaim_available
        ? `The ${fundedSide === "seller" ? "buyer" : "seller"} did not fund within ${cfg?.escrow_timeout_hours}h. The ${fundedSide} can reclaim their deposit now.`
        : <>If the other side does not fund, the {fundedSide} can reclaim in <span className="mono text-warn">{countdown(deal.seconds_to_reclaim)}</span>.</>}
    </div>
  </div>
);

const TicketActions = ({ deal, cfg, w, d, pending, onAct }) => (
  <div className="border-t border-line p-4 space-y-2">
    {!w.connected && d.live && <div className="text-xs text-slate-400" data-testid="escrow-connect-hint">Connect a wallet to fund a side. The signature is your authorization; funds go straight to the escrow contract.</div>}
    {w.connected && d.canFundSeller && (
      <button onClick={() => onAct({ action: "fund", side: "seller" })} disabled={pending} className="btn-primary w-full" data-testid="escrow-seller-fund-btn">
        <ShieldCheck size={15} /> Fund seller side · {d.sellerLabel}
      </button>
    )}
    {w.connected && d.canFundBuyer && (
      <button onClick={() => onAct({ action: "fund", side: "buyer" })} disabled={pending} className="btn-primary w-full" data-testid="escrow-buyer-fund-btn">
        <ShieldCheck size={15} /> Fund buyer side · {fmtUsd(deal.notional_usd)} + fee
      </button>
    )}
    {w.connected && d.iAmFunded && (
      <button onClick={() => onAct({ action: "reclaim" })} disabled={!deal.reclaim_available || pending} className="btn-danger w-full" data-testid="escrow-reclaim-btn">
        <Undo2 size={15} /> {deal.reclaim_available ? "Reclaim my deposit" : `Reclaim opens in ${countdown(deal.seconds_to_reclaim)}`}
      </button>
    )}
    {w.connected && d.isCreator && deal.status === "open" && (
      <button onClick={() => onAct({ action: "cancel" })} disabled={pending} className="btn-ghost w-full text-slate-400" data-testid="escrow-cancel-btn"><XCircle size={15} /> Withdraw listing</button>
    )}
    {w.connected && d.live && !d.canFundSeller && !d.canFundBuyer && !d.iAmFunded && !(d.isCreator && deal.status === "open") && (
      <div className="text-xs text-slate-400 mono" data-testid="escrow-waiting-msg">Your side is funded. Waiting for the {d.isSeller ? "buyer" : "seller"} to deposit.</div>
    )}
    {!d.live && <div className="text-xs text-fog" data-testid="escrow-closed-msg">This escrow is closed ({deal.status}). No further actions.</div>}
    <div className="mono text-[10px] text-slate-600 pt-1">Contract {short(cfg?.escrow_contract)} · Robinhood Chain {cfg?.chain_id}{cfg?.simulated && " · on-chain calls simulated"}</div>
  </div>
);

export const EscrowTicket = ({ deal, onChanged }) => {
  const w = useWallet();
  const qc = useQueryClient();
  const { data: cfg } = useConfig();
  const openShare = useShare();

  const act = useMutation({
    mutationFn: async ({ action, side }) => {
      if (action === "fund") return api.fund(deal.id, { ...(await w.signAction(`fund_${side}`, deal.id)), side });
      if (action === "reclaim") return api.reclaim(deal.id, await w.signAction("reclaim", deal.id));
      return api.cancel(deal.id, await w.signAction("cancel", deal.id));
    },
    onSuccess: (updated, vars) => {
      qc.setQueryData(["deal", updated.id], updated);
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["settled"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      onChanged?.(updated);
      if (updated.status === "settled") toast.success("Both sides funded — escrow released automatically.", { action: { label: "Share", onClick: () => openShare(updated) } });
      else if (vars.action === "fund") toast.success(`${vars.side === "seller" ? "Tokens" : "USD + fee"} deposited. Waiting for the other side.`);
      else if (vars.action === "reclaim") toast.success("Deposit reclaimed. Escrow closed.");
      else toast.success("Listing withdrawn.");
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!deal) return <TicketEmpty cfg={cfg} />;

  const st = STATUS[deal.status];
  const stock = deal.token?.stock || deal.stock;
  const d = deriveTicket(deal, w.address);

  return (
    <div className="flex flex-col h-full" data-testid="escrow-ticket-container">
      <TicketHeader deal={deal} stock={stock} st={st} />

      <div className="flex-1 overflow-auto px-4 py-3 space-y-4">
        <SlippageBadge deal={deal} stock={stock} />

        <div>
          <div className="eyebrow mb-1">Escrow status</div>
          <EscrowSteps deal={deal} sellerLabel={d.sellerLabel} buyerLabel={d.buyerLabel} />
          {deal.status === "settled" && (
            <div className="space-y-2">
              <div className="mono text-[11px] text-pos" data-testid="escrow-settled-msg">Both sides funded — released automatically at {new Date(deal.settled_at).toUTCString().slice(5, 25)}.</div>
              <button onClick={() => openShare(deal)} className="btn-ghost h-8 px-3 text-xs" data-testid="escrow-share-btn"><Share2 size={13} /> Share settled card</button>
            </div>
          )}
        </div>

        <RequiredDeposits deal={deal} sellerLabel={d.sellerLabel} />

        {d.fundedSide && <TimeoutBox deal={deal} fundedSide={d.fundedSide} cfg={cfg} />}
      </div>

      <TicketActions deal={deal} cfg={cfg} w={w} d={d} pending={act.isPending} onAct={act.mutate} />
    </div>
  );
};
